import { type Board, type Library, type ListResponse, SortDirection, type SubnavBoardSort } from '@air/api/types';
import { type InfiniteData, type QueryKey, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { chunk } from 'lodash';
import { useCallback } from 'react';

import {
  getLibraryRootBoardsKey,
  LIBRARY_BOARDS_LIST,
} from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { getPrivateBoardsListKey } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useFavoriteBoardsCacheUpdate } from '~/components/v2/hooks/useFavoriteBoardsCacheUpdate';
import { useSubBoardsCacheUpdate } from '~/components/v2/hooks/useSubBoardsCacheUpdate';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { getFavoriteBoardsKey } from '~/swr-hooks/boards/useFavoriteBoardsList';
import { getRootWorkspaceBoardsKey } from '~/swr-hooks/boards/useRootWorkspaceBoards';
import { BOARD_SUBBOARDS, getBoardSubBoardsKey } from '~/swr-hooks/boards/useSubBoardsList';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import { insertIf } from '~/utils/insertIf';
import { getOrderedBoards } from '~/utils/mutateUtils/getOrderedBoards';
import {
  defaultBoardsState,
  getRearrangedBoards,
  getUpdatedBoards,
  type UpdatedBoard,
} from '~/utils/mutateUtils/mutators';

// Optimistically adjust directDescendantBoardCount for parent boards within V2 caches only
const adjustParentDirectChildCount = (
  queryClient: ReturnType<typeof useQueryClient>,
  {
    parentBoardId,
    delta,
    boardSort,
    workspaceId,
    librariesFlag,
    libraryId,
  }: {
    parentBoardId: string;
    delta: number;
    boardSort?: SubnavBoardSort;
    workspaceId: string;
    librariesFlag?: boolean;
    libraryId?: string;
  },
) => {
  // Update workspace root cache (no-op if board isn't present there)
  const workspaceKey = getPrivateBoardsListKey({
    isMemberOfBoard: 'ALL',
    limit: 100,
    view: 'root',
    includeAncestors: true,
    includeDirectDescendantBoardCount: true,
    numThumbnails: 0,
    sortField: boardSort ?? {
      direction: SortDirection.desc,
      name: 'customSort',
    },
    libraryBoards: librariesFlag ? 'NO' : 'ALL',
    workspaceId,
  });

  queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey: workspaceKey }, (data) => {
    if (!data) return data;
    return produce(data, (draft) => {
      let found = false;
      draft.pages.forEach((page) => {
        const idx = page.data.findIndex((b) => b.id === parentBoardId);
        if (idx !== -1) {
          found = true;
          const current = page.data[idx].directDescendantBoardCount ?? 0;
          const newCount = Math.max(0, current + delta);
          page.data[idx].directDescendantBoardCount = newCount;
        }
      });
      if (!found) {
        // Board not found in workspace cache - this is normal for library boards
      }
    });
  });

  // Update library root cache if applicable (no-op if board isn't present there)
  if (libraryId) {
    const libraryKey = getLibraryRootBoardsKey({ workspaceId, libraryId, sortField: boardSort });
    queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey: libraryKey }, (data) => {
      if (!data) return data;
      return produce(data, (draft) => {
        draft.pages.forEach((page) => {
          const idx = page.data.findIndex((b) => b.id === parentBoardId);
          if (idx !== -1) {
            const current = page.data[idx].directDescendantBoardCount ?? 0;
            page.data[idx].directDescendantBoardCount = Math.max(0, current + delta);
          }
        });
      });
    });
  }
};

/**
 * Updates the directDescendantBoardCount for parent boards when boards are moved
 * Invalidates board list queries to ensure fresh data with updated counts
 */
const updateParentBoardChildCount = (
  queryClient: ReturnType<typeof useQueryClient>,
  parentBoardId: string | null,
  _countDelta: number,
  _boardSort?: SubnavBoardSort,
  _librariesFlag?: boolean,
  workspaceId?: string,
  _libraryId?: string,
  enableUpdates = false,
) => {
  // Only enable cache invalidation when called from V2 components
  if (!enableUpdates) {
    return;
  }

  if (!parentBoardId || !workspaceId) return;

  // Invalidate all queries that might contain this parent board
  // This ensures the UI refetches with correct directDescendantBoardCount
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      if (!Array.isArray(queryKey)) return false;

      // Invalidate private boards list queries (workspace boards)
      if (queryKey[0] === 'PRIVATE_BOARDS_LIST') {
        return true;
      }

      // Invalidate library boards queries
      if (queryKey[0] === 'LIBRARY_BOARDS_LIST') {
        return true;
      }

      // Invalidate favorite boards queries
      if (queryKey[0] === 'FAVORITE_BOARDS') {
        return true;
      }

      // Invalidate the old BOARDS_LIST queries (legacy)
      if (queryKey[0] === 'BOARDS_LIST') {
        return true;
      }

      return false;
    },
  });
};

// Helper to generate consistent cache key for general workspace (matches shared useRootWorkspaceBoards)
const getGeneralWorkspaceCacheKey = (workspaceId: string, sortField?: SubnavBoardSort, librariesFlag?: boolean) => {
  // GeneralLibraryItem uses the shared useRootWorkspaceBoards hook, which creates BOARDS_LIST cache
  return getRootWorkspaceBoardsKey({
    workspaceId,
    sortField,
    libraryBoards: librariesFlag ? ('NO' as const) : ('ALL' as const),
  });
};

type SetSideNavQueryDataParams = {
  cacheKeys: QueryKey[];
  updater: (data?: InfiniteData<ListResponse<Board>>) => InfiniteData<ListResponse<Board>> | undefined;
  revalidate?: boolean;
};

const useSetSideNavQueryData = () => {
  const queryClient = useQueryClient();

  const setSideNavQueryData = useCallback(
    ({ cacheKeys, updater, revalidate }: SetSideNavQueryDataParams) => {
      cacheKeys.forEach((key) => {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey: key }, updater);
      });

      if (revalidate) {
        return Promise.all(cacheKeys.map(async (key) => queryClient.invalidateQueries({ queryKey: key })));
      }
    },
    [queryClient],
  );

  return {
    setSideNavQueryData,
  };
};

export const removeBoards =
  (boardIds: string[]) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    if (!data) return;

    return produce(data, (draft) => {
      draft.pages.forEach((page) => {
        page.data = page.data.filter((b) => !boardIds.includes(b.id));
      });
    });
  };

export const addBoards =
  (boards: Board[], boardSort: SubnavBoardSort = { name: '', direction: SortDirection.asc }) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    return produce(data || defaultBoardsState, (draft) => {
      const flattenedBoards = draft.pages.flatMap((b) => b.data);
      flattenedBoards.push(...boards);

      const { orderedBoards } = getOrderedBoards(flattenedBoards, boardSort);
      const chunkedBoards = chunk(orderedBoards, Math.ceil(orderedBoards.length / draft?.pages.length));

      chunkedBoards.forEach((chunk, index) => {
        draft.pages[index].data = chunk;
      });
    });
  };

export const useUpdateSideNavBoard = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  /**
   * Use this method to update board in side nav
   */
  const updateSideNavBoard = useCallback(
    (updatedBoard: UpdatedBoard, subnavSortBy?: SubnavBoardSort) => {
      try {
        const subNavSort = getSubNavSortValue();

        const cacheKeys = [
          // update favorite boards if board is in favorites
          ...insertIf(updatedBoard.hasCurrentUser, () => [getFavoriteBoardsKey(updatedBoard.workspaceId)]),
          // update workspace boards if board has no parentId
          ...insertIf(!updatedBoard.parentId, () => [
            getRootWorkspaceBoardsKey({ workspaceId: updatedBoard.workspaceId, sortField: subNavSort?.boardSort }),
          ]),
          // update subboards if board has parentId
          ...insertIf(!!updatedBoard.parentId, () => [
            getBoardSubBoardsKey(updatedBoard.parentId!, subNavSort?.boardSort),
          ]),
          // update library board if board has library
          ...insertIf(!!updatedBoard?.library?.id, () => [
            getLibraryRootBoardsKey({
              workspaceId: updatedBoard.workspaceId,
              libraryId: updatedBoard.library!.id,
              sortField: subNavSort?.boardSort,
            }),
          ]),
        ];
        return setSideNavQueryData({ cacheKeys, updater: getUpdatedBoards(updatedBoard, subnavSortBy) });
      } catch (error) {
        console.error({ error });
      }
    },
    [getSubNavSortValue, setSideNavQueryData],
  );

  return {
    updateSideNavBoard,
  };
};

export const useRemoveBoardsFromSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();

  /**
   * Use this method to remove boards from side nav
   */
  const removeBoardsFromSideNav = useCallback(
    ({
      workspaceId,
      boardIds,
      parentId,
      libraryId,
      navSortParams,
    }: {
      workspaceId: string;
      boardIds: string[];
      /*
       Pass '*' to remove board from all places in sidenav, regardless of parentId or libraryId
       */
      parentId?: string | null | '*';
      navSortParams?: SubnavBoardSort;
      libraryId?: Library['id'];
    }) => {
      const cacheKeys = [
        getFavoriteBoardsKey(workspaceId),
        ...insertIf((!parentId && !libraryId) || parentId === '*', () => [
          getGeneralWorkspaceCacheKey(workspaceId, navSortParams, librariesFlag),
        ]),
        ...insertIf((!parentId || parentId === '*') && !!libraryId, () => [
          getLibraryRootBoardsKey({ libraryId, workspaceId, sortField: navSortParams }),
        ]),
        ...insertIf(parentId === '*' && !libraryId, () => [[LIBRARY_BOARDS_LIST]]),
        parentId && parentId !== '*' ? getBoardSubBoardsKey(parentId, navSortParams) : [BOARD_SUBBOARDS],
      ];

      const result = setSideNavQueryData({ cacheKeys, updater: removeBoards(boardIds) });

      // Special handling: If removing from a library but the board might also be in General Library,
      // try removing from General Library as well (fallback for mismatched board data)
      if (libraryId && !parentId) {
        const generalLibraryKey = getGeneralWorkspaceCacheKey(workspaceId, navSortParams, librariesFlag);
        setSideNavQueryData({ cacheKeys: [generalLibraryKey], updater: removeBoards(boardIds) });
      }

      return result;
    },
    [setSideNavQueryData, librariesFlag],
  );

  return {
    removeBoardsFromSideNav,
  };
};

export const useToggleFavoritedSideNavBoard = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();

  const toggleFavoritedSideNavBoard = useCallback(
    (board: Board) => {
      const { workspaceId, hasCurrentUser, id } = board;

      if (hasCurrentUser) {
        setSideNavQueryData({ cacheKeys: [getFavoriteBoardsKey(workspaceId)], updater: addBoards([board]) });
      } else {
        setSideNavQueryData({ cacheKeys: [getFavoriteBoardsKey(workspaceId)], updater: removeBoards([id]) });
      }

      const keysToUpdate = [
        getRootWorkspaceBoardsKey({ workspaceId }),
        // TODO check if getLibraryRootBoardsKey should be added
        // by passing '*' we mutate all board subboards cache keys and don't need parentId
        [BOARD_SUBBOARDS],
      ];

      return setSideNavQueryData({ cacheKeys: keysToUpdate, updater: getUpdatedBoards({ id, hasCurrentUser }) });
    },
    [setSideNavQueryData],
  );

  return {
    toggleFavoritedSideNavBoard,
  };
};

export const useAddBoardsToSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();

  const addBoardsToSideNav = useCallback(
    (boards: Board[], boardSort?: SubnavBoardSort) => {
      const cacheKeys = [];
      const libraryId = boards[0].library?.id;

      // currently it is only possible to add multiple boards at once to one parent (by duplicating them)
      if (!boards[0].parentId && libraryId) {
        cacheKeys.push(
          getLibraryRootBoardsKey({
            workspaceId: boards[0].workspaceId,
            sortField: boardSort,
            libraryId: boards[0].library!.id,
          }),
        );
      } else if (!boards[0].parentId) {
        cacheKeys.push(getGeneralWorkspaceCacheKey(boards[0].workspaceId, boardSort, librariesFlag));
      } else {
        cacheKeys.push(getBoardSubBoardsKey(boards[0].parentId, boardSort));
      }

      return setSideNavQueryData({ cacheKeys, updater: addBoards(boards, boardSort) });
    },
    [setSideNavQueryData, librariesFlag],
  );

  return {
    addBoardsToSideNav,
  };
};

export const useChangeSideBoardsParent = (enableParentCountUpdates = false) => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();
  const { addBoardsToSubBoardsCache, removeBoardsFromSubBoardsCache } = useSubBoardsCacheUpdate();
  const { ui2 } = useTemporaryFeatureFlags();
  const queryClient = useQueryClient();

  const changeSideBoardsParent = useCallback(
    async (
      workspaceId: string,
      newBoards: Board[],
      oldParentId?: string | null,
      oldLibraryId: string | undefined = undefined,
    ) => {
      try {
        const boardSort = getSubNavSortValue()?.boardSort;
        const newParentId = newBoards[0].parentId;
        const newLibraryId = newBoards[0].library?.id;

        let keyToRemove, keyToAdd;

        // Update board library data before cache operations for library moves
        const updatedBoards = newBoards.map((board) => {
          if (board.library?.id !== newLibraryId) {
            return {
              ...board,
              library: newLibraryId ? ({ id: newLibraryId } as Board['library']) : undefined,
            };
          }
          return board;
        });

        if (oldParentId) {
          keyToRemove = getBoardSubBoardsKey(oldParentId, boardSort);
        } else if (oldLibraryId) {
          keyToRemove = getLibraryRootBoardsKey({ workspaceId, sortField: boardSort, libraryId: oldLibraryId });
        } else {
          keyToRemove = getGeneralWorkspaceCacheKey(workspaceId, boardSort, librariesFlag);
        }

        if (newParentId) {
          keyToAdd = getBoardSubBoardsKey(newParentId, boardSort);
        } else if (newLibraryId) {
          keyToAdd = getLibraryRootBoardsKey({ workspaceId, sortField: boardSort, libraryId: newLibraryId });
        } else {
          keyToAdd = getGeneralWorkspaceCacheKey(workspaceId, boardSort, librariesFlag);
        }

        const updatePromises = [
          setSideNavQueryData({ cacheKeys: [keyToRemove], updater: removeBoards(newBoards.map((board) => board.id)) }),
        ];

        // Only add to main cache if not using v2 path
        if (!ui2) {
          updatePromises.push(setSideNavQueryData({ cacheKeys: [keyToAdd], updater: addBoards(updatedBoards) }));
        }

        // Update parent board child counts after a brief delay to ensure other cache updates are complete
        // Only run this for V2 components to avoid affecting V1 behavior
        if (enableParentCountUpdates) {
          setTimeout(() => {
            const boardCount = newBoards.length;

            // Decrease count for old parent
            if (oldParentId) {
              // optimistic local count adjust
              adjustParentDirectChildCount(queryClient, {
                parentBoardId: oldParentId,
                delta: -boardCount,
                boardSort,
                workspaceId,
                librariesFlag,
                libraryId: oldLibraryId,
              });

              updateParentBoardChildCount(
                queryClient,
                oldParentId,
                -boardCount,
                boardSort,
                librariesFlag,
                workspaceId,
                oldLibraryId,
                enableParentCountUpdates,
              );
            }

            // Increase count for new parent
            if (newParentId) {
              // optimistic local count adjust
              adjustParentDirectChildCount(queryClient, {
                parentBoardId: newParentId,
                delta: boardCount,
                boardSort,
                workspaceId,
                librariesFlag,
                libraryId: newLibraryId,
              });

              updateParentBoardChildCount(
                queryClient,
                newParentId,
                boardCount,
                boardSort,
                librariesFlag,
                workspaceId,
                newLibraryId,
                enableParentCountUpdates,
              );
            }
          }, 100);
        }

        // Update v2 subboards cache if needed
        if (ui2) {
          // Remove from old parent's cache
          if (oldParentId) {
            removeBoardsFromSubBoardsCache({
              parentBoard: { id: oldParentId },
              sort: boardSort,
              boardIds: newBoards.map((board) => board.id),
            });
          }

          // Add to new parent's cache (subboard)
          if (newParentId) {
            addBoardsToSubBoardsCache({
              parentBoard: { id: newParentId },
              sort: boardSort,
              boards: updatedBoards,
            });
          }
          // For root-level moves, use the main cache update mechanism
          else {
            updatePromises.push(setSideNavQueryData({ cacheKeys: [keyToAdd], updater: addBoards(updatedBoards) }));
          }
        }

        return await Promise.all(updatePromises);
      } catch (error) {
        console.error({ error });
      }
    },
    [
      getSubNavSortValue,
      setSideNavQueryData,
      librariesFlag,
      addBoardsToSubBoardsCache,
      removeBoardsFromSubBoardsCache,
      ui2,
      queryClient,
      enableParentCountUpdates,
    ],
  );

  return {
    changeSideBoardsParent,
  };
};

export const useRearrangeBoardsInSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { updateSubBoardsCache } = useSubBoardsCacheUpdate();
  const { ui2 } = useTemporaryFeatureFlags();
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();

  const rearrangeBoardsInSideNav = useCallback(
    (rearrangedBoards: Board[]) => {
      const boardSort = getSubNavSortValue()?.boardSort;

      const newParentId = rearrangedBoards[0].parentId;
      const newLibraryId = rearrangedBoards[0].library?.id;
      let keyToUpdate;

      if (newParentId) {
        keyToUpdate = getBoardSubBoardsKey(newParentId, boardSort);

        if (ui2) {
          updateSubBoardsCache({
            parentBoard: { id: newParentId },
            sort: boardSort,
            rearrangedBoards,
          });
        }
      } else if (newLibraryId) {
        keyToUpdate = getLibraryRootBoardsKey({
          workspaceId: rearrangedBoards[0].workspaceId,
          sortField: boardSort,
          libraryId: newLibraryId,
        });
      } else {
        keyToUpdate = getGeneralWorkspaceCacheKey(rearrangedBoards[0].workspaceId, boardSort, librariesFlag);
      }

      return setSideNavQueryData({ cacheKeys: [keyToUpdate], updater: getRearrangedBoards(rearrangedBoards) });
    },
    [getSubNavSortValue, setSideNavQueryData, updateSubBoardsCache, ui2, librariesFlag],
  );

  return {
    rearrangeBoardsInSideNav,
  };
};

export const useRearrangeFavoriteBoardsInSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { updateFavoriteBoardsCache } = useFavoriteBoardsCacheUpdate();
  const { ui2 } = useTemporaryFeatureFlags();

  const rearrangeFavoriteBoardsInSideNav = useCallback(
    (rearrangedBoards: Board[]) => {
      setSideNavQueryData({
        cacheKeys: [getFavoriteBoardsKey(rearrangedBoards[0].workspaceId)],
        updater: getRearrangedBoards(rearrangedBoards),
      });

      if (ui2) {
        updateFavoriteBoardsCache({ rearrangedBoards });
      }
    },
    [setSideNavQueryData, updateFavoriteBoardsCache, ui2],
  );

  return {
    rearrangeFavoriteBoardsInSideNav,
  };
};

export const useRefreshSideNavBoards = () => {
  const queryClient = useQueryClient();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  const refreshRootSideNavBoards = useCallback(
    (workspaceId: string) => {
      const subNavSort = getSubNavSortValue();
      return queryClient.invalidateQueries({
        queryKey: getRootWorkspaceBoardsKey({ workspaceId, sortField: subNavSort?.boardSort }),
      });
    },
    [getSubNavSortValue, queryClient],
  );

  const refreshSideNavSubBoards = useCallback(
    (boardId: string) => {
      const subNavSort = getSubNavSortValue();
      queryClient.invalidateQueries({ queryKey: getBoardSubBoardsKey(boardId, subNavSort?.boardSort) });
    },
    [getSubNavSortValue, queryClient],
  );

  return {
    refreshRootSideNavBoards,
    refreshSideNavSubBoards,
  };
};
